import { createDrawerView } from './drawer-view';
import { attachDrawerResize } from './drawer-resize';
import { mountDrawer, type DrawerTabId } from './shell-drawer';
import type { DrawerDOMRefs, DrawerShellApi, DrawerView } from '../core/types';
import type { DrawerUIState } from '../services/ui-state';
import { loadDrawerUIState, saveDrawerUIState } from '../services/ui-state';
import { isRevealPrintPdfMode } from '../reveal-print-core';

export interface CreateDrawerControllerOptions {
  mountPoint: HTMLElement;
  dom: DrawerDOMRefs;
  presentationId: string;
  /** Remote presenter: open drawer on Speaker notes tab instead of persisted UI state. */
  remotePresenterInitialSpeaker?: boolean;
  onOpenChange?: (open: boolean) => void;
  onTabChange?: (tab: DrawerTabId) => void;
}

export interface DrawerControllerResult {
  drawerApi: DrawerShellApi;
  drawerView: DrawerView;
}

interface AdaptiveLayoutController {
  setOpen(open: boolean): void;
  setUserDrawerWidthPx(px: number): void;
  setUserStackedHeightPx(px: number): void;
  resetUserDrawerWidth(): void;
  resetUserStackedHeight(): void;
  getDrawerWidthBounds(): { min: number; max: number };
  getStackedHeightBounds(): { min: number; max: number };
  getAppliedDrawerWidthPx(): number;
  getAppliedStackedHeightPx(): number;
  dispose(): void;
}

const MIN_DRAWER_WIDTH = 260;
const MAX_DRAWER_WIDTH = 720;
const MIN_SLIDE_STRIP = 200;

function createAdaptiveLayoutController(
  mountPoint: HTMLElement,
  initial: DrawerUIState,
  onLayoutChange?: () => void
): AdaptiveLayoutController {
  const DESKTOP_DRAWER_WIDTH = 380;
  const STACKED_DRAWER_MIN_HEIGHT = 240;
  const STACKED_DRAWER_MAX_HEIGHT = 460;
  const WIDTH_HYSTERESIS = 36;
  const HEIGHT_HYSTERESIS = 28;
  /** Remote shell: mount height is only the main band (below header/footer), so slidesHeightWhenOpen is always small vs full-viewport decks — use an absolute floor instead of comparing to drawer height. */
  const REMOTE_AUTO_FULLSCREEN_MAX_SLIDE_STRIP_PX = 80;
  let open = false;
  let stackedMode = false;
  let fullscreenMode = false;
  let userDrawerWidthPx: number | null =
    typeof initial.drawerWidthPx === 'number' && Number.isFinite(initial.drawerWidthPx)
      ? initial.drawerWidthPx
      : null;
  let userStackedHeightPx: number | null =
    typeof initial.stackedDrawerHeightPx === 'number' && Number.isFinite(initial.stackedDrawerHeightPx)
      ? initial.stackedDrawerHeightPx
      : null;

  function maxDrawerWidthForMount(): number {
    const w = mountPoint.clientWidth || 0;
    return Math.max(MIN_DRAWER_WIDTH, Math.min(MAX_DRAWER_WIDTH, w - MIN_SLIDE_STRIP));
  }

  function clampDrawerWidthPx(px: number): number {
    return Math.round(Math.min(maxDrawerWidthForMount(), Math.max(MIN_DRAWER_WIDTH, px)));
  }

  function clampStackedHeightPx(px: number): number {
    return Math.round(Math.min(STACKED_DRAWER_MAX_HEIGHT, Math.max(STACKED_DRAWER_MIN_HEIGHT, px)));
  }

  function getViewportHeight(): number {
    const vv = window.visualViewport;
    if (vv && Number.isFinite(vv.height) && vv.height > 0) return Math.round(vv.height);
    return window.innerHeight || mountPoint.clientHeight || 0;
  }

  /**
   * Height of the slides+drawer mount. On some hosts (e.g. remote: nested flex + height:100%),
   * clientHeight can be 0 before layout or when percentage height fails to resolve. If we then
   * compute slidesHeightWhenOpen as 0, adaptive layout wrongly enables rf-layout-fullscreen
   * (drawer always full-screen). Fall back to parent box or viewport.
   */
  function resolveMountHeight(): number {
    const ch = mountPoint.clientHeight;
    if (ch > 0) return ch;
    const rect = mountPoint.getBoundingClientRect().height;
    if (Number.isFinite(rect) && rect > 0.5) return Math.round(rect);
    const parent = mountPoint.parentElement;
    if (parent && parent.clientHeight > 0) return parent.clientHeight;
    return getViewportHeight();
  }

  function getStackedDrawerHeight(): number {
    const target = Math.round(getViewportHeight() * 0.45);
    return Math.max(STACKED_DRAWER_MIN_HEIGHT, Math.min(STACKED_DRAWER_MAX_HEIGHT, target));
  }

  function updateLayoutMode(): void {
    const wrapWidth = mountPoint.clientWidth || 0;
    const wrapHeight = resolveMountHeight();
    const enterStackedAt = DESKTOP_DRAWER_WIDTH * 2 - WIDTH_HYSTERESIS;
    const exitStackedAt = DESKTOP_DRAWER_WIDTH * 2 + WIDTH_HYSTERESIS;
    if (!stackedMode && wrapWidth > 0 && wrapWidth <= enterStackedAt) stackedMode = true;
    else if (stackedMode && wrapWidth >= exitStackedAt) stackedMode = false;

    const widthPx = clampDrawerWidthPx(userDrawerWidthPx ?? DESKTOP_DRAWER_WIDTH);
    mountPoint.style.setProperty('--rf-drawer-desktop-width', `${widthPx}px`);
    mountPoint.classList.toggle('rf-layout-stacked', stackedMode);

    if (!stackedMode) {
      fullscreenMode = false;
      mountPoint.classList.remove('rf-layout-fullscreen');
      mountPoint.style.removeProperty('--rf-stacked-drawer-open-height');
      return;
    }

    const stackedDrawerHeight = clampStackedHeightPx(userStackedHeightPx ?? getStackedDrawerHeight());
    mountPoint.style.setProperty('--rf-stacked-drawer-open-height', `${stackedDrawerHeight}px`);
    const slidesHeightWhenOpen = Math.max(0, wrapHeight - stackedDrawerHeight);
    const inRemoteShell =
      typeof mountPoint.closest === 'function' && mountPoint.closest('.remote-shell') != null;
    // Presentation: "squished" means slide strip smaller than ~one drawer height. Remote: mount is already short; only auto-fullscreen if slide strip is unusably tiny.
    const enterFullscreenAt = inRemoteShell
      ? REMOTE_AUTO_FULLSCREEN_MAX_SLIDE_STRIP_PX
      : stackedDrawerHeight - HEIGHT_HYSTERESIS;
    const exitFullscreenAt = inRemoteShell
      ? REMOTE_AUTO_FULLSCREEN_MAX_SLIDE_STRIP_PX + HEIGHT_HYSTERESIS
      : stackedDrawerHeight + HEIGHT_HYSTERESIS;
    /** User-chosen stacked height must not trigger auto-fullscreen (fixed inset:0); that mode hides the resize handle and traps mobile users. */
    const userOwnsStackedHeight = userStackedHeightPx !== null;
    if (!open) {
      fullscreenMode = false;
    } else if (userOwnsStackedHeight) {
      fullscreenMode = false;
    } else if (!fullscreenMode && slidesHeightWhenOpen <= enterFullscreenAt) {
      fullscreenMode = true;
    } else if (fullscreenMode && slidesHeightWhenOpen > exitFullscreenAt) {
      fullscreenMode = false;
    }
    mountPoint.classList.toggle('rf-layout-fullscreen', fullscreenMode);
  }

  const onResize = (): void => {
    updateLayoutMode();
    onLayoutChange?.();
  };
  window.addEventListener('resize', onResize, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', onResize, { passive: true });
  }

  updateLayoutMode();
  return {
    setOpen(nextOpen: boolean): void {
      open = nextOpen;
      updateLayoutMode();
      onLayoutChange?.();
    },
    setUserDrawerWidthPx(px: number): void {
      userDrawerWidthPx = clampDrawerWidthPx(px);
      updateLayoutMode();
      onLayoutChange?.();
    },
    setUserStackedHeightPx(px: number): void {
      userStackedHeightPx = clampStackedHeightPx(px);
      updateLayoutMode();
      onLayoutChange?.();
    },
    resetUserDrawerWidth(): void {
      userDrawerWidthPx = null;
      updateLayoutMode();
      onLayoutChange?.();
    },
    resetUserStackedHeight(): void {
      userStackedHeightPx = null;
      updateLayoutMode();
      onLayoutChange?.();
    },
    getDrawerWidthBounds(): { min: number; max: number } {
      return { min: MIN_DRAWER_WIDTH, max: maxDrawerWidthForMount() };
    },
    getStackedHeightBounds(): { min: number; max: number } {
      return { min: STACKED_DRAWER_MIN_HEIGHT, max: STACKED_DRAWER_MAX_HEIGHT };
    },
    getAppliedDrawerWidthPx(): number {
      const raw = mountPoint.style.getPropertyValue('--rf-drawer-desktop-width').trim();
      const m = raw.match(/^([\d.]+)px$/);
      if (m) return Math.round(parseFloat(m[1]));
      return clampDrawerWidthPx(userDrawerWidthPx ?? DESKTOP_DRAWER_WIDTH);
    },
    getAppliedStackedHeightPx(): number {
      const raw = mountPoint.style.getPropertyValue('--rf-stacked-drawer-open-height').trim();
      const m = raw.match(/^([\d.]+)px$/);
      if (m) return Math.round(parseFloat(m[1]));
      return clampStackedHeightPx(userStackedHeightPx ?? getStackedDrawerHeight());
    },
    dispose(): void {
      window.removeEventListener('resize', onResize as EventListener);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', onResize as EventListener);
      }
    }
  };
}

function createHostLayoutNotifier(): (withSettlePass?: boolean) => void {
  let pendingLayoutRaf: number | null = null;
  let settleTimeoutId: number | null = null;

  function applyHostLayout(): void {
    if (typeof Reveal !== 'undefined') {
      try {
        Reveal.layout?.();
      } catch {
        try {
          Reveal.configure?.({});
        } catch {
          // Ignore host relayout failures.
        }
      }
    }
  }

  return function notifyHostLayout(withSettlePass: boolean = true): void {
    if (pendingLayoutRaf !== null) cancelAnimationFrame(pendingLayoutRaf);
    pendingLayoutRaf = requestAnimationFrame(() => {
      pendingLayoutRaf = null;
      applyHostLayout();
      requestAnimationFrame(() => {
        applyHostLayout();
      });
    });
    if (!withSettlePass) return;
    if (settleTimeoutId !== null) clearTimeout(settleTimeoutId);
    settleTimeoutId = window.setTimeout(() => {
      settleTimeoutId = null;
      applyHostLayout();
    }, 230);
  };
}

export function createDrawerController(opts: CreateDrawerControllerOptions): DrawerControllerResult {
  const notifyHostLayout = createHostLayoutNotifier();
  const drawerState = loadDrawerUIState(opts.presentationId);
  const adaptiveLayout = createAdaptiveLayoutController(opts.mountPoint, drawerState, () =>
    notifyHostLayout(false)
  );
  const initialSpeaker = opts.remotePresenterInitialSpeaker === true;
  const printPdf = isRevealPrintPdfMode();
  /** Persisted "drawer open" must not apply on ?summary / print-handout (chat/notes are not for print). */
  const initialOpen = printPdf ? false : initialSpeaker ? true : drawerState.open;
  const initialTab = printPdf ? drawerState.tab : initialSpeaker ? 'speaker' : drawerState.tab;
  const drawerApi = mountDrawer(opts.mountPoint, opts.dom, {
    initialOpen,
    initialTab,
    onOpenChange: (isOpen: boolean) => {
      adaptiveLayout.setOpen(isOpen);
      if (!printPdf) {
        saveDrawerUIState(opts.presentationId, { open: isOpen });
      }
      opts.onOpenChange?.(isOpen);
      notifyHostLayout();
    },
    onTabChange: (tab: DrawerTabId) => {
      if (!printPdf) {
        saveDrawerUIState(opts.presentationId, { tab });
      }
      opts.onTabChange?.(tab);
    }
  });
  const onDrawerTransitionEnd = (ev: TransitionEvent): void => {
    if (ev.target !== opts.dom.drawer) return;
    if (ev.propertyName !== 'width' && ev.propertyName !== 'height' && ev.propertyName !== 'min-height' && ev.propertyName !== 'transform') {
      return;
    }
    notifyHostLayout(false);
  };
  opts.dom.drawer.addEventListener('transitionend', onDrawerTransitionEnd);
  const drawerView = createDrawerView(drawerApi, opts.dom);

  attachDrawerResize({
    mountPoint: opts.mountPoint,
    drawer: opts.dom.drawer,
    resizeHandle: opts.dom.resizeHandle,
    getStacked: () => opts.mountPoint.classList.contains('rf-layout-stacked'),
    getDesktopFullscreen: () => opts.mountPoint.classList.contains('rf-layout-desktop-fullscreen'),
    getMobileFullscreen: () => opts.mountPoint.classList.contains('rf-layout-mobile-fullscreen'),
    setDrawerWidthPx: (px) => adaptiveLayout.setUserDrawerWidthPx(px),
    setStackedHeightPx: (px) => adaptiveLayout.setUserStackedHeightPx(px),
    getDrawerWidthBounds: () => adaptiveLayout.getDrawerWidthBounds(),
    getStackedHeightBounds: () => adaptiveLayout.getStackedHeightBounds(),
    getCurrentDrawerWidth: () => adaptiveLayout.getAppliedDrawerWidthPx(),
    getCurrentStackedHeight: () => adaptiveLayout.getAppliedStackedHeightPx(),
    onPersistWidth: (px) => {
      saveDrawerUIState(opts.presentationId, { drawerWidthPx: px });
    },
    onPersistStackedHeight: (px) => {
      saveDrawerUIState(opts.presentationId, { stackedDrawerHeightPx: px });
    },
    onResetWidth: () => {
      adaptiveLayout.resetUserDrawerWidth();
      saveDrawerUIState(opts.presentationId, { resetDrawerWidth: true });
    },
    onResetStackedHeight: () => {
      adaptiveLayout.resetUserStackedHeight();
      saveDrawerUIState(opts.presentationId, { resetStackedDrawerHeight: true });
    },
    onResizeTick: () => notifyHostLayout(false),
    getStackedResizeSeamFromBottom: () =>
      typeof opts.mountPoint.closest === 'function' &&
      opts.mountPoint.closest('.remote-shell') != null &&
      opts.mountPoint.classList.contains('rf-layout-stacked')
  });
  return { drawerApi, drawerView };
}
